import { $generateHtmlFromNodes } from '@lexical/html';
import { LexicalEditor } from 'lexical';

// The backend sanitizes chat message content down to these elements (see
// Organizations::Message#content=), so anything else would be stripped
// server-side anyway.
const ALLOWED_ELEMENTS = ['P', 'BR', 'STRONG', 'EM', 'U', 'UL', 'OL', 'LI'];

// Lexical exports bold as <b><strong> and italic as <i><em>, but the backend
// sanitizer only accepts <strong> and <em> (the format draft-js produced).
const RENAMED_ELEMENTS: { [tag: string]: string } = { B: 'STRONG', I: 'EM' };

const stripAttributes = (element: Element): void => {
  while (element.attributes.length > 0) {
    element.removeAttribute(element.attributes[0].name);
  }
};

// Collapse the doubled tags left behind by renaming, e.g.
// <strong><strong>x</strong></strong> becomes <strong>x</strong>.
const unwrapSameTagOnlyChild = (element: Element): void => {
  const child = element.firstElementChild;
  if (element.childNodes.length === 1 && child && child.tagName === element.tagName) {
    while (child.firstChild) {
      element.insertBefore(child.firstChild, child);
    }
    element.removeChild(child);
  }
};

const normalizeChildren = (node: Element, doc: Document): void => {
  Array.from(node.children).forEach((child) => normalizeElement(child, doc));
};

const normalizeElement = (element: Element, doc: Document): void => {
  normalizeChildren(element, doc);

  const renamedTag = RENAMED_ELEMENTS[element.tagName];
  if (renamedTag) {
    const renamed = doc.createElement(renamedTag);
    while (element.firstChild) {
      renamed.appendChild(element.firstChild);
    }
    element.replaceWith(renamed);
    unwrapSameTagOnlyChild(renamed);
  } else if (ALLOWED_ELEMENTS.indexOf(element.tagName) === -1) {
    element.replaceWith(...Array.from(element.childNodes));
  } else {
    stripAttributes(element);
    unwrapSameTagOnlyChild(element);
  }
};

// Must be called within an editor read or update context.
export const exportChatHtml = (editor: LexicalEditor): string => {
  const doc = new DOMParser().parseFromString($generateHtmlFromNodes(editor), 'text/html');
  normalizeChildren(doc.body, doc);
  return doc.body.innerHTML;
};
